import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import AlertLineStatus from './AlertLineStatus';
import { ANNOTATION_COLORS } from '../../hooks/useChartAnnotations';
import useClickOutside from '../../hooks/useClickOutside';

// Offset from click position to avoid covering annotation
const POPOVER_OFFSET = 20;
// Minimum distance from viewport edges
const VIEWPORT_PADDING = 16;

const LINE_STYLES = [
  { value: 'solid', displayLabel: '───' },
  { value: 'dashed', displayLabel: '- - -' },
  { value: 'dotted', displayLabel: '···' },
];

/** Display labels for system timeframe values */
const TIMEFRAME_LABELS = {
  daily: '1D',
  hourly: '1H',
  weekly: '1W',
  monthly: '1M',
  '4h': '4H',
  '1h': '1H',
  '1d': '1D',
  '1w': '1W',
  '1M': '1M',
};

/**
 * Format a price to an appropriate number of decimal places
 */
const formatPrice = (price) => {
  if (price == null || !Number.isFinite(price)) return '';
  if (Math.abs(price) < 1) return price.toPrecision(4);
  if (Math.abs(price) < 10) return price.toFixed(4);
  return price.toFixed(2);
};

const EMOJI_PRESETS = [
  '🔥', '🚀', '💎', '🐂', '🐻', '⚠️', '🎯', '💰',
  '📈', '📉', '🛑', '✅', '❌', '⭐', '👀', '🦊',
  '💡', '🔔', '📍', '🏁', '🤔', '💀', '🎉', '🔑',
];

/**
 * Popover for editing annotation properties
 */
const AnnotationEditPopover = ({
  annotation,
  position,
  onUpdate,
  onDelete,
  onClose,
  onRearmAlert,
  currentTimeframe,
  availableTimeframes = [],
}) => {
  const popoverRef = useRef(null);
  const [label, setLabel] = useState(annotation?.label || annotation?.text || '');
  const [priceInput, setPriceInput] = useState(() => formatPrice(annotation?.price));
  const [isPriceDirty, setIsPriceDirty] = useState(false);

  // --- Drag state ---
  const [dragOffset, setDragOffset] = useState({ dx: 0, dy: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef(null); // { mouseX, mouseY, dx, dy }

  // --- Smart positioning state ---
  const [adjustedPosition, setAdjustedPosition] = useState(null);
  const lastPositionRef = useRef(null);

  // Reset drag offset + drag state + price + adjusted position when a different annotation is selected
  useEffect(() => {
    setDragOffset({ dx: 0, dy: 0 });
    setIsDragging(false);
    dragStart.current = null;
    setPriceInput(formatPrice(annotation?.price));
    setIsPriceDirty(false);
  }, [annotation?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset adjusted position before paint when annotation or position changes
  useLayoutEffect(() => {
    const nextPosition = position ? { x: position.x, y: position.y } : null;
    const lastPosition = lastPositionRef.current;
    const positionChanged = !lastPosition
      || !nextPosition
      || lastPosition.x !== nextPosition.x
      || lastPosition.y !== nextPosition.y;

    if (positionChanged || lastPosition?.annotationId !== annotation?.id) {
      setAdjustedPosition(null);
      lastPositionRef.current = nextPosition
        ? { ...nextPosition, annotationId: annotation?.id ?? null }
        : null;
    }
  }, [annotation?.id, position?.x, position?.y]);

  // Smart positioning: measure popover after render and adjust to avoid going off-screen
  // or covering the likely annotation area
  useLayoutEffect(() => {
    if (!popoverRef.current || !position || adjustedPosition) return;
    
    const rect = popoverRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Start with offset from click position (to avoid covering the click point/annotation)
    let newX = position.x + POPOVER_OFFSET;
    let newY = position.y + POPOVER_OFFSET;
    
    // Check if popover would go off the right edge
    if (newX + rect.width > viewportWidth - VIEWPORT_PADDING) {
      // Try positioning to the left of the click instead
      newX = position.x - rect.width - POPOVER_OFFSET;
    }
    
    // Check if it would go off the left edge
    if (newX < VIEWPORT_PADDING) {
      newX = VIEWPORT_PADDING;
    }
    
    // Check if popover would go off the bottom edge
    if (newY + rect.height > viewportHeight - VIEWPORT_PADDING) {
      // Try positioning above the click instead
      newY = position.y - rect.height - POPOVER_OFFSET;
    }
    
    // Check if it would go off the top edge
    if (newY < VIEWPORT_PADDING) {
      newY = VIEWPORT_PADDING;
    }
    
    // Only update if position actually changed from raw click position
    if (newX !== position.x || newY !== position.y) {
      setAdjustedPosition({ x: newX, y: newY });
    } else {
      // Mark as calculated even if unchanged
      setAdjustedPosition({ x: newX, y: newY });
    }
  }, [position, adjustedPosition]);

  // Update label when annotation changes
  useEffect(() => {
    setLabel(annotation?.label || annotation?.text || '');
  }, [annotation?.id, annotation?.label, annotation?.text]);

  // Update price input when annotation price changes externally
  useEffect(() => {
    setPriceInput(formatPrice(annotation?.price));
    setIsPriceDirty(false);
  }, [annotation?.price]);

  // --- Drag handlers (attached to window so drag continues outside popover) ---
  const handleMouseMove = useCallback((e) => {
    if (!dragStart.current) return;
    const { mouseX, mouseY, dx: startDx, dy: startDy } = dragStart.current;
    setDragOffset({
      dx: startDx + (e.clientX - mouseX),
      dy: startDy + (e.clientY - mouseY),
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    dragStart.current = null;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleHeaderMouseDown = (e) => {
    // Don't start drag if clicking the close button
    if (e.target.closest('button')) return;
    e.preventDefault();
    dragStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      dx: dragOffset.dx,
      dy: dragOffset.dy,
    };
    setIsDragging(true);
  };

  // Close on outside click (Escape is handled globally by useChartAnnotations hook)
  useClickOutside(popoverRef, onClose);

  if (!annotation) return null;

  const isHLine = annotation.type === 'hline';
  const isVLine = annotation.type === 'vline';
  const isCrossLine = annotation.type === 'crossline';
  const isText = annotation.type === 'text';
  const isTrendLine = annotation.type === 'trendline';
  const isRay = annotation.type === 'ray';
  const isRectangle = annotation.type === 'rectangle';
  const isEmoji = annotation.type === 'emoji';
  const isTimeRange = annotation.type === 'timerange';
  const isArrow = annotation.type === 'arrow';
  const isChannel = annotation.type === 'channel';
  const isFibRetracement = annotation.type === 'fibRetracement';
  const isMeasure = annotation.type === 'measure';
  const isAlertLine = annotation.type === 'alert_line';
  const isBrush = annotation.type === 'brush';
  const isPitchfork = annotation.type === 'pitchfork';
  const timeframeChips = availableTimeframes;

  const handleLabelChange = (newLabel) => {
    setLabel(newLabel);
    if (isHLine || isTimeRange || isChannel || isAlertLine || isPitchfork) {
      onUpdate?.(annotation.id, { label: newLabel });
    } else if (isText || isArrow) {
      onUpdate?.(annotation.id, { text: newLabel });
    } else {
      onUpdate?.(annotation.id, { label: newLabel });
    }
  };

  const handleColorChange = (color) => {
    onUpdate?.(annotation.id, { color });
  };

  const handleLineStyleChange = (lineStyle) => {
    if (isHLine || isVLine || isTrendLine || isRay || isCrossLine || isRectangle || isTimeRange || isArrow || isChannel || isFibRetracement || isMeasure || isAlertLine || isPitchfork) {
      onUpdate?.(annotation.id, { lineStyle });
    }
  };

  const handleDirectionChange = (direction) => {
    if (isAlertLine) {
      onUpdate?.(annotation.id, { direction });
    }
  };

  const handleEmojiChange = (emoji) => {
    onUpdate?.(annotation.id, { emoji });
  };

  const handleEmojiSizeChange = (size) => {
    onUpdate?.(annotation.id, { size });
  };

  const handleFillOpacityChange = (fillOpacity) => {
    if (isRectangle || isTimeRange || isChannel) {
      onUpdate?.(annotation.id, { fillOpacity });
    }
  };

  const formatTime = (t) => {
    if (t == null || !Number.isFinite(t)) return '';
    const d = new Date(t);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().replace('T', ' ').slice(0, 16);
  };
  // Use adjusted position if available, otherwise fall back to raw position
  const effectivePosition = adjustedPosition || position;
  
  const popoverStyle = {
    position: 'fixed',
    top: (effectivePosition?.y ?? 100) + dragOffset.dy,
    left: (effectivePosition?.x ?? 100) + dragOffset.dx,
    backgroundColor: 'var(--dwlf-elevated)',
    border: '1px solid var(--dwlf-ink-20)',
    borderRadius: 8,
    padding: 12,
    minWidth: 220,
    maxHeight: 'calc(100vh - 32px)', // Don't exceed viewport height
    overflowY: 'auto',
    boxShadow: '0 8px 24px rgb(0 0 0 / 0.35)',
    zIndex: 10000,
    color: 'var(--dwlf-text)',
  };

  return (
    <div ref={popoverRef} style={popoverStyle}>
      {/* Header (draggable) */}
      <div
        onMouseDown={handleHeaderMouseDown}
        style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: 12,
          paddingBottom: 8,
          borderBottom: '1px solid var(--dwlf-ink-12)',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600 }}>
          {{ hline: 'Horizontal Line', vline: 'Vertical Line', crossline: 'Cross Line', text: 'Text Label', trendline: 'Trend Line', ray: 'Ray', rectangle: 'Price Range', emoji: 'Emoji Marker', timerange: 'Time Range', arrow: 'Arrow / Callout', channel: 'Parallel Channel', fibRetracement: 'Fib Retracement', measure: 'Measure', alert_line: '🔔 Alert Line', brush: 'Freehand Drawing', pitchfork: 'Pitchfork' }[annotation.type] || 'Annotation'}
        </span>
        <button
          onClick={() => onClose?.()}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--dwlf-ink-55)',
            cursor: 'pointer',
            fontSize: 16,
            padding: 0,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {/* Label/Text Input */}
      {!isEmoji && !isBrush && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 4 }}>
            {isText ? 'TEXT' : 'LABEL'}
          </label>
          {isText || isArrow ? (
            <textarea
              value={label}
              onChange={(e) => handleLabelChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
                  e.preventDefault();
                  onClose?.();
                }
              }}
              placeholder={isText ? "Enter text..." : isArrow ? "Callout text..." : "Enter text..."}
              rows={3}
              style={{
                width: '100%',
                padding: '6px 8px',
                backgroundColor: 'var(--dwlf-sunk)',
                border: '1px solid var(--dwlf-ink-20)',
                borderRadius: 4,
                color: 'var(--dwlf-text)',
                fontSize: 12,
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
              maxLength={500}
              autoFocus
            />
          ) : (
            <input
              type="text"
              value={label}
              onChange={(e) => handleLabelChange(e.target.value)}
              placeholder={isHLine ? 'Price level label...' : isAlertLine ? 'Alert label...' : isRectangle ? 'Zone label...' : isTimeRange ? 'Zone label...' : isChannel ? 'Channel label...' : 'Label...'}
              style={{
                width: '100%',
                padding: '6px 8px',
                backgroundColor: 'var(--dwlf-sunk)',
                border: '1px solid var(--dwlf-ink-20)',
                borderRadius: 4,
                color: 'var(--dwlf-text)',
                fontSize: 12,
                outline: 'none',
              }}
              autoFocus
            />
          )}
        </div>
      )}

      {/* Emoji Picker */}
      {isEmoji && (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 6 }}>
              EMOJI
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {EMOJI_PRESETS.map((e) => (
                <button
                  key={e}
                  onClick={() => handleEmojiChange(e)}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 4,
                    backgroundColor: annotation.emoji === e ? 'var(--dwlf-accent-30)' : 'var(--dwlf-ink-10)',
                    border: annotation.emoji === e ? '2px solid var(--dwlf-accent)' : '2px solid transparent',
                    cursor: 'pointer',
                    fontSize: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.1s ease',
                  }}
                  onMouseEnter={(ev) => {
                    ev.currentTarget.style.transform = 'scale(1.15)';
                  }}
                  onMouseLeave={(ev) => {
                    ev.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 6 }}>
              SIZE
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="range"
                min="16"
                max="48"
                step="4"
                value={annotation.size ?? 24}
                onChange={(ev) => handleEmojiSizeChange(parseInt(ev.target.value, 10))}
                style={{
                  flex: 1,
                  accentColor: 'var(--dwlf-accent)',
                  cursor: 'pointer',
                }}
              />
              <span style={{ fontSize: 11, color: 'var(--dwlf-ink-55)', fontFamily: 'monospace', minWidth: 28 }}>
                {annotation.size ?? 24}px
              </span>
            </div>
          </div>
        </>
      )}

      {/* Color Palette (not for emoji) */}
      {!isEmoji && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 6 }}>
            COLOR
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {ANNOTATION_COLORS.map((color) => (
              <button
                key={color}
                onClick={() => handleColorChange(color)}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 4,
                  backgroundColor: color,
                  border: annotation.color === color ? '2px solid var(--dwlf-text)' : '2px solid transparent',
                  cursor: 'pointer',
                  transition: 'transform 0.1s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Direction toggle (Alert Line only) */}
      {isAlertLine && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 6 }}>
            ALERT DIRECTION
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { value: 'above', icon: '▲', desc: 'Crosses Above' },
              { value: 'below', icon: '▼', desc: 'Crosses Below' },
            ].map(({ value, icon, desc }) => (
              <button
                key={value}
                onClick={() => handleDirectionChange(value)}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  backgroundColor: annotation.direction === value ? 'var(--dwlf-warn)' : 'var(--dwlf-ink-10)',
                  border: '1px solid var(--dwlf-ink-20)',
                  borderRadius: 4,
                  color: annotation.direction === value ? 'var(--dwlf-on-accent)' : 'var(--dwlf-text)',
                  fontSize: 12,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                }}
              >
                <span>{icon}</span>
                <span>{desc}</span>
              </button>
            ))}
          </div>
          <AlertLineStatus annotation={annotation} onRearmAlert={onRearmAlert} />
        </div>
      )}

      {/* Line Style (line-based tools) */}
      {(isHLine || isVLine || isTrendLine || isRay || isCrossLine || isRectangle || isTimeRange || isArrow || isChannel || isFibRetracement || isMeasure || isAlertLine || isPitchfork) && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 6 }}>
            LINE STYLE
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            {/* Issue #15 fix: renamed destructured var to avoid shadowing state 'label' */}
            {LINE_STYLES.map(({ value, displayLabel }) => (
              <button
                key={value}
                onClick={() => handleLineStyleChange(value)}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  backgroundColor: annotation.lineStyle === value ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-10)',
                  border: '1px solid var(--dwlf-ink-20)',
                  borderRadius: 4,
                  color: annotation.lineStyle === value ? 'var(--dwlf-on-accent)' : 'var(--dwlf-text)',
                  fontSize: 12,
                  cursor: 'pointer',
                  fontFamily: 'monospace',
                }}
              >
                {displayLabel}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Extend toggles (Trend Line and Channel) */}
      {(isTrendLine || isChannel) && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 6 }}>
            EXTEND
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => onUpdate?.(annotation.id, { extendLeft: !annotation.extendLeft })}
              style={{
                flex: 1,
                padding: '6px 8px',
                backgroundColor: annotation.extendLeft ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-10)',
                border: '1px solid var(--dwlf-ink-20)',
                borderRadius: 4,
                color: annotation.extendLeft ? 'var(--dwlf-on-accent)' : 'var(--dwlf-text)',
                fontSize: 11,
                cursor: 'pointer',
              }}
            >
              ← Left
            </button>
            <button
              onClick={() => onUpdate?.(annotation.id, { extendRight: !annotation.extendRight })}
              style={{
                flex: 1,
                padding: '6px 8px',
                backgroundColor: annotation.extendRight ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-10)',
                border: '1px solid var(--dwlf-ink-20)',
                borderRadius: 4,
                color: annotation.extendRight ? 'var(--dwlf-on-accent)' : 'var(--dwlf-text)',
                fontSize: 11,
                cursor: 'pointer',
              }}
            >
              Right →
            </button>
          </div>
        </div>
      )}

      {/* Extend Right toggle (Pitchfork only) */}
      {isPitchfork && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 6 }}>
            EXTEND
          </label>
          <button
            onClick={() => onUpdate?.(annotation.id, { extendRight: !annotation.extendRight })}
            style={{
              width: '100%',
              padding: '6px 8px',
              backgroundColor: annotation.extendRight ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-10)',
              border: '1px solid var(--dwlf-ink-20)',
              borderRadius: 4,
              color: annotation.extendRight ? 'var(--dwlf-on-accent)' : 'var(--dwlf-text)',
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            {annotation.extendRight ? '✓ Extend Right' : 'Extend Right →'}
          </button>
        </div>
      )}

      {/* Fill opacity (Pitchfork only) */}
      {isPitchfork && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 6 }}>
            FILL OPACITY
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="range"
              min="0"
              max="0.5"
              step="0.02"
              value={annotation.fillOpacity ?? 0.06}
              onChange={(e) => onUpdate?.(annotation.id, { fillOpacity: parseFloat(e.target.value) })}
              style={{
                flex: 1,
                accentColor: annotation.color,
              }}
            />
            <span style={{ fontSize: 11, color: 'var(--dwlf-ink-70)', fontFamily: 'monospace', minWidth: 30 }}>
              {Math.round((annotation.fillOpacity ?? 0.06) * 100)}%
            </span>
          </div>
        </div>
      )}

      {/* Price input (HLine / Alert Line) */}
      {(isHLine || isAlertLine) && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 4 }}>
            PRICE LEVEL
          </label>
          <input
            type="text"
            value={priceInput}
            onChange={(e) => {
              setPriceInput(e.target.value);
              setIsPriceDirty(true);
            }}
            onBlur={() => {
              if (!isPriceDirty) {
                setPriceInput(formatPrice(annotation.price));
                setIsPriceDirty(false);
                return;
              }
              const parsed = parseFloat(priceInput);
              if (Number.isFinite(parsed)) {
                if (!Number.isFinite(annotation.price) || parsed !== annotation.price) {
                  onUpdate?.(annotation.id, { price: parsed });
                  // Keep the submitted value visible until the prop updates
                  setPriceInput(formatPrice(parsed));
                } else {
                  setPriceInput(formatPrice(annotation.price));
                }
              } else {
                setPriceInput(formatPrice(annotation.price));
              }
              setIsPriceDirty(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.target.blur();
              }
            }}
            style={{
              width: '100%',
              padding: '6px 8px',
              backgroundColor: 'var(--dwlf-sunk)',
              border: '1px solid var(--dwlf-ink-20)',
              borderLeft: `3px solid ${annotation.color}`,
              borderRadius: 4,
              color: 'var(--dwlf-text)',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'monospace',
              outline: 'none',
            }}
          />
        </div>
      )}

      {/* Time display (VLine only) */}
      {isVLine && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 4 }}>
            DATE / TIME
          </label>
          <div style={{ 
            fontSize: 13, 
            fontWeight: 600, 
            color: annotation.color,
            fontFamily: 'monospace'
          }}>
            {formatTime(annotation.time)}
          </div>
        </div>
      )}

      {/* Cross Line specific: show price/time toggles + info */}
      {isCrossLine && (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 6 }}>
              SHOW LABELS
            </label>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => onUpdate?.(annotation.id, { showPrice: !annotation.showPrice })}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  backgroundColor: annotation.showPrice ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-10)',
                  border: '1px solid var(--dwlf-ink-20)',
                  borderRadius: 4,
                  color: annotation.showPrice ? 'var(--dwlf-on-accent)' : 'var(--dwlf-text)',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                💰 Price
              </button>
              <button
                onClick={() => onUpdate?.(annotation.id, { showTime: !annotation.showTime })}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  backgroundColor: annotation.showTime ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-10)',
                  border: '1px solid var(--dwlf-ink-20)',
                  borderRadius: 4,
                  color: annotation.showTime ? 'var(--dwlf-on-accent)' : 'var(--dwlf-text)',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                🕐 Time
              </button>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 4 }}>
              INTERSECTION
            </label>
            <div style={{ 
              fontSize: 12, 
              fontWeight: 600, 
              color: annotation.color,
              fontFamily: 'monospace'
            }}>
              {formatPrice(annotation.price)}
              {annotation.time && (
                <span style={{ color: 'var(--dwlf-ink-55)', fontWeight: 400, marginLeft: 8 }}>
                  {new Date(annotation.time).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </>
      )}

      {/* Rectangle specific: fill opacity + price range */}
      {isRectangle && (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 6 }}>
              FILL OPACITY
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="range"
                min="0"
                max="0.5"
                step="0.05"
                value={annotation.fillOpacity ?? 0.15}
                onChange={(e) => handleFillOpacityChange(parseFloat(e.target.value))}
                style={{
                  flex: 1,
                  accentColor: annotation.color || '#3b82f6',
                  cursor: 'pointer',
                }}
              />
              <span style={{ fontSize: 11, color: 'var(--dwlf-ink-55)', fontFamily: 'monospace', minWidth: 32 }}>
                {Math.round((annotation.fillOpacity ?? 0.15) * 100)}%
              </span>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 4 }}>
              PRICE RANGE
            </label>
            <div style={{ 
              fontSize: 12, 
              fontWeight: 600, 
              color: annotation.color,
              fontFamily: 'monospace',
              display: 'flex',
              justifyContent: 'space-between',
            }}>
              <span>{Math.max(annotation.price1 ?? 0, annotation.price2 ?? 0).toFixed(2)}</span>
              <span style={{ color: 'var(--dwlf-ink-55)', fontWeight: 400 }}>→</span>
              <span>{Math.min(annotation.price1 ?? 0, annotation.price2 ?? 0).toFixed(2)}</span>
            </div>
            <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>
              Δ {Math.abs((annotation.price2 ?? 0) - (annotation.price1 ?? 0)).toFixed(2)}
            </div>
          </div>
        </>
      )}

      {/* Timeframe Visibility — only show when multiple timeframes available */}
      {timeframeChips.length > 1 && <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 6 }}>
          VISIBLE ON
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {/* "All" toggle */}
          <button
            onClick={() => {
              onUpdate?.(annotation.id, { visibleTimeframes: [] });
            }}
            style={{
              padding: '4px 8px',
              backgroundColor: (!annotation.visibleTimeframes || annotation.visibleTimeframes.length === 0) ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-10)',
              border: '1px solid var(--dwlf-ink-20)',
              borderRadius: 4,
              color: (!annotation.visibleTimeframes || annotation.visibleTimeframes.length === 0) ? 'var(--dwlf-on-accent)' : 'var(--dwlf-text)',
              fontSize: 10,
              cursor: 'pointer',
              fontWeight: (!annotation.visibleTimeframes || annotation.visibleTimeframes.length === 0) ? 600 : 400,
            }}
          >
            All
          </button>
          {timeframeChips.map((tf) => {
            const isActive = annotation.visibleTimeframes?.includes(tf);
            const isCurrent = tf === currentTimeframe;
            const chipLabel = TIMEFRAME_LABELS[tf] || tf;
            return (
              <button
                key={tf}
                onClick={() => {
                  const current = annotation.visibleTimeframes || [];
                  let next;
                  if (isActive) {
                    next = current.filter(t => t !== tf);
                  } else {
                    next = [...current, tf];
                  }
                  onUpdate?.(annotation.id, { visibleTimeframes: next });
                }}
                style={{
                  padding: '4px 8px',
                  backgroundColor: isActive ? (annotation.color || '#3b82f6') : 'var(--dwlf-ink-10)',
                  border: isCurrent ? '1px solid var(--dwlf-text)' : '1px solid var(--dwlf-ink-20)',
                  borderRadius: 4,
                  color: 'var(--dwlf-text)',
                  fontSize: 10,
                  cursor: 'pointer',
                  fontWeight: isActive ? 600 : 400,
                  opacity: isActive ? 1 : 0.7,
                }}
              >
                {chipLabel}
              </button>
            );
          })}
        </div>
      </div>}
      {/* Time Range specific: fill opacity */}
      {isTimeRange && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 6 }}>
            FILL OPACITY
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="range"
              min="0"
              max="0.5"
              step="0.05"
              value={annotation.fillOpacity ?? 0.12}
              onChange={(e) => handleFillOpacityChange(parseFloat(e.target.value))}
              style={{
                flex: 1,
                accentColor: annotation.color || '#8b5cf6',
                cursor: 'pointer',
              }}
            />
            <span style={{ fontSize: 11, color: 'var(--dwlf-ink-55)', fontFamily: 'monospace', minWidth: 32 }}>
              {Math.round((annotation.fillOpacity ?? 0.12) * 100)}%
            </span>
          </div>
        </div>
      )}

      {/* Fill opacity (Channel only) */}
      {isChannel && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 6 }}>
            FILL OPACITY
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="range"
              min="0"
              max="0.5"
              step="0.02"
              value={annotation.fillOpacity ?? 0.08}
              onChange={(e) => onUpdate?.(annotation.id, { fillOpacity: parseFloat(e.target.value) })}
              style={{
                flex: 1,
                accentColor: annotation.color,
              }}
            />
            <span style={{ fontSize: 11, color: 'var(--dwlf-ink-70)', fontFamily: 'monospace', minWidth: 30 }}>
              {Math.round((annotation.fillOpacity ?? 0.08) * 100)}%
            </span>
          </div>
        </div>
      )}

      {/* Fibonacci Retracement specific: fill opacity and extend options */}
      {isFibRetracement && (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 6 }}>
              FILL OPACITY
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="range"
                min="0"
                max="0.3"
                step="0.02"
                value={annotation.fillOpacity ?? 0.06}
                onChange={(e) => onUpdate?.(annotation.id, { fillOpacity: parseFloat(e.target.value) })}
                style={{
                  flex: 1,
                  accentColor: annotation.color,
                }}
              />
              <span style={{ fontSize: 11, color: 'var(--dwlf-ink-70)', fontFamily: 'monospace', minWidth: 30 }}>
                {Math.round((annotation.fillOpacity ?? 0.06) * 100)}%
              </span>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 6 }}>
              EXTEND
            </label>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => onUpdate?.(annotation.id, { extendRight: !annotation.extendRight })}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  backgroundColor: annotation.extendRight ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-10)',
                  border: '1px solid var(--dwlf-ink-20)',
                  borderRadius: 4,
                  color: annotation.extendRight ? 'var(--dwlf-on-accent)' : 'var(--dwlf-text)',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                Extend Right →
              </button>
            </div>
          </div>
        </>
      )}

      {/* Measure specific: display options */}
      {isMeasure && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 6 }}>
            DISPLAY
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => onUpdate?.(annotation.id, { showPriceDelta: !annotation.showPriceDelta })}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  backgroundColor: annotation.showPriceDelta ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-10)',
                  border: '1px solid var(--dwlf-ink-20)',
                  borderRadius: 4,
                  color: annotation.showPriceDelta ? 'var(--dwlf-on-accent)' : 'var(--dwlf-text)',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                💰 Price Δ
              </button>
              <button
                onClick={() => onUpdate?.(annotation.id, { showTimeDelta: !annotation.showTimeDelta })}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  backgroundColor: annotation.showTimeDelta ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-10)',
                  border: '1px solid var(--dwlf-ink-20)',
                  borderRadius: 4,
                  color: annotation.showTimeDelta ? 'var(--dwlf-on-accent)' : 'var(--dwlf-text)',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                🕐 Time Δ
              </button>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => onUpdate?.(annotation.id, { showPercentage: !annotation.showPercentage })}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  backgroundColor: annotation.showPercentage ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-10)',
                  border: '1px solid var(--dwlf-ink-20)',
                  borderRadius: 4,
                  color: annotation.showPercentage ? 'var(--dwlf-on-accent)' : 'var(--dwlf-text)',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                📊 %
              </button>
              <button
                onClick={() => onUpdate?.(annotation.id, { showDistance: !annotation.showDistance })}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  backgroundColor: annotation.showDistance ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-10)',
                  border: '1px solid var(--dwlf-ink-20)',
                  borderRadius: 4,
                  color: annotation.showDistance ? 'var(--dwlf-on-accent)' : 'var(--dwlf-text)',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                📏 Distance
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Line Width (Brush only) */}
      {isBrush && (
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 10, color: 'var(--dwlf-ink-55)', display: 'block', marginBottom: 6 }}>
            LINE WIDTH
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            {[1, 2, 3, 4, 6].map((w) => (
              <button
                key={w}
                onClick={() => onUpdate?.(annotation.id, { lineWidth: w })}
                style={{
                  flex: 1,
                  padding: '6px 4px',
                  backgroundColor: annotation.lineWidth === w ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-10)',
                  border: '1px solid var(--dwlf-ink-20)',
                  borderRadius: 4,
                  color: annotation.lineWidth === w ? 'var(--dwlf-on-accent)' : 'var(--dwlf-text)',
                  fontSize: 11,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div style={{
                  width: 16,
                  height: w,
                  backgroundColor: annotation.color || '#fff',
                  borderRadius: w / 2,
                }} />
              </button>
            ))}
          </div>
          <div style={{ fontSize: 10, color: 'var(--dwlf-ink-45)', marginTop: 4, textAlign: 'center' }}>
            {annotation.points?.length ?? 0} points
          </div>
        </div>
      )}

      {/* Delete Button */}
      <button
        onClick={() => {
          onDelete?.(annotation.id);
          onClose?.();
        }}
        style={{
          width: '100%',
          padding: '8px 12px',
          backgroundColor: 'transparent',
          border: '1px solid #ef4444',
          borderRadius: 4,
          color: '#ef4444',
          fontSize: 12,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          transition: 'background-color 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <span>🗑️</span>
        Delete
      </button>

      {/* Keyboard hint */}
      <div style={{ 
        marginTop: 8, 
        fontSize: 10, 
        color: '#666',
        textAlign: 'center'
      }}>
        Press Delete key or ESC to close
      </div>
    </div>
  );
};

AnnotationEditPopover.propTypes = {
  annotation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['hline', 'vline', 'crossline', 'text', 'trendline', 'ray', 'rectangle', 'emoji', 'timerange', 'arrow', 'channel', 'fibRetracement', 'measure', 'alert_line', 'brush', 'pitchfork', 'long_position', 'short_position']).isRequired,
    color: PropTypes.string,
    label: PropTypes.string,
    text: PropTypes.string,
    lineStyle: PropTypes.string,
    price: PropTypes.number,
    time: PropTypes.number,
    visibleTimeframes: PropTypes.arrayOf(PropTypes.string),
    showPrice: PropTypes.bool,
    showTime: PropTypes.bool,
    extendLeft: PropTypes.bool,
    extendRight: PropTypes.bool,
    price1: PropTypes.number,
    price2: PropTypes.number,
    fillOpacity: PropTypes.number,
    emoji: PropTypes.string,
    size: PropTypes.number,
  }),
  position: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  onUpdate: PropTypes.func,
  onDelete: PropTypes.func,
  onRearmAlert: PropTypes.func,
  onClose: PropTypes.func,
  currentTimeframe: PropTypes.string,
  availableTimeframes: PropTypes.arrayOf(PropTypes.string),
};

export default AnnotationEditPopover;
